import { Disclosure } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

interface QA {
  q: string
  a: React.ReactNode
}

const FAQS: QA[] = [
  {
    q: 'How accurate is it?',
    a: (
      <>
        Matching runs through a 5-stage ML pipeline layered on curated deterministic rules, tuned for
        high precision. It is not perfect &mdash; when it gets something wrong, report it and the
        models retrain on the correction.
      </>
    ),
  },
  {
    q: 'Which sports do you support?',
    a: (
      <>
        Eight: soccer, basketball, tennis, American football, volleyball, ice hockey, baseball and
        rugby. Every plan includes all of them.
      </>
    ),
  },
  {
    q: 'How fast is it?',
    a: (
      <>
        Cached lookups return in sub-millisecond time. A cold match runs the pipeline in a few
        milliseconds, and large event batches resolve in seconds.
      </>
    ),
  },
  {
    q: 'How do I get a key?',
    a: (
      <>
        Register for an account and you&apos;ll receive an API token immediately. Our team then
        activates a plan on it &mdash; there&apos;s no self-serve billing yet.
      </>
    ),
  },
  {
    q: 'Do you store my data?',
    a: (
      <>
        Requests are logged for quality monitoring, debugging and improving the models. We don&apos;t
        sell or share your data.
      </>
    ),
  },
  {
    q: 'Can I match whole fixture lists?',
    a: (
      <>
        Yes &mdash; the <span className="font-mono text-dash-text">/api/match-events</span> endpoint
        reconciles fixtures across multiple sources in a single call. See the{' '}
        <Link to="/docs" className="text-dash-accent underline-offset-2 hover:underline">
          docs
        </Link>{' '}
        for the request shape.
      </>
    ),
  },
]

export function FAQ() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
      <h2 className="text-center text-3xl font-bold tracking-tight text-dash-text sm:text-4xl">
        Frequently asked questions
      </h2>

      <dl className="mt-12 space-y-3">
        {FAQS.map((item) => (
          <Disclosure key={item.q} as="div" className="rounded-xl border border-dash-border bg-dash-surface">
            {({ open }) => (
              <>
                <dt>
                  <Disclosure.Button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent rounded-xl">
                    <span className="text-base font-medium text-dash-text">{item.q}</span>
                    <ChevronDownIcon
                      className={`h-5 w-5 shrink-0 text-dash-secondary transition-transform duration-200 ${
                        open ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="px-5 pb-5 text-sm leading-relaxed text-dash-secondary">
                  {item.a}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ))}
      </dl>
    </section>
  )
}
